import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState({ en: "", it: "" });

  const load = () => http.get("/admin/reports").then((r) => setReports(r.data.reports || []));
  useEffect(() => { load(); }, []);

  const filtered = showResolved ? reports : reports.filter((r) => r.status !== "RESOLVED");

  const resolve = async () => {
    if (!note.en.trim()) { toast.error("A public changelog note is required to resolve."); return; }
    try {
      await http.post(`/admin/reports/${active.id}/resolve`, { resolution_note_en: note.en, resolution_note_it: note.it });
      toast.success("Report resolved & public changelog updated");
      setActive(null); setNote({ en: "", it: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl">Error reports inbox</h1>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="show-resolved" /> Show resolved
          </label>
        </div>

        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Submitted</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Category</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Description</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">SLA</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const overdue = (r.sla_days_remaining ?? 0) < 0;
                const soon = (r.sla_days_remaining ?? 0) >= 0 && (r.sla_days_remaining ?? 0) < 3;
                return (
                  <tr key={r.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`report-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{(r.submitted_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-mono">{r.brand_slug || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.category_hint || "—"}</td>
                    <td className="px-4 py-3 max-w-md truncate">{r.description}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${overdue ? "text-red-700 font-bold" : soon ? "text-amber-700" : ""}`}>
                      {r.sla_days_remaining == null ? "—" : overdue ? `${Math.abs(r.sla_days_remaining)}d OVERDUE` : `${r.sla_days_remaining}d`}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.status}</td>
                    <td className="px-4 py-3 text-right">
                      {r.status !== "RESOLVED" && (
                        <button onClick={() => setActive(r)} className="text-sm bp-link" data-testid={`report-resolve-${r.id}`}>Resolve →</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: "var(--bp-text-muted)" }}>Inbox clear.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">Resolve report</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4 text-sm">
              <div className="p-3 border" style={{ borderColor: "var(--bp-border)", background: "var(--bp-bg-secondary)" }}>
                <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Brand: {active.brand_slug} · Category: {active.category_hint || "—"}</div>
                <p className="mt-1">{active.description}</p>
                {active.link && <a href={active.link} target="_blank" rel="noopener noreferrer" className="text-xs bp-link">{active.link}</a>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest">Public changelog note (EN) — required</label>
                <Textarea required value={note.en} onChange={(e) => setNote({ ...note, en: e.target.value })} data-testid="resolve-note-en" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest">Public changelog note (IT) — optional</label>
                <Textarea value={note.it} onChange={(e) => setNote({ ...note, it: e.target.value })} data-testid="resolve-note-it" />
              </div>
              <Button onClick={resolve} className="rounded-none w-full" data-testid="resolve-submit">Resolve & publish changelog entry</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
